import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import Checkbox from 'components/elements/atoms/Checkbox';
import { Icon, ICONS } from 'components/Icons';
import classNames from 'classnames';

import './TableColumnFilter.scss';
import { FocusTrap } from 'focus-trap-react';

export default function TableColumnFilter({
  options,
  onChange,
  value,
  single = true,
  showAll = false,
  allLabel = 'All',
  allowSearch = false,
  filterElm,
  columnName,
}) {
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const rafRef = useRef(null);
  const lastRectRef = useRef(null);
  const poInnerRef = useRef(null);

  const repositionManual = useCallback(() => {
    const inner = poInnerRef.current;
    const pop = (inner && inner.closest && inner.closest('.popover')) || inner;
    const target = triggerRef.current;

    if (!pop || !target || !target.getBoundingClientRect) {
      return;
    }

    const tr = target.getBoundingClientRect();
    const pr = pop.getBoundingClientRect();
    let left = tr.left + (tr.width - pr.width) / 2;
    let top = tr.bottom;
    left = Math.max(8, Math.min(left, window.innerWidth - pr.width - 8));

    if (top + pr.height > window.innerHeight) {
      top = tr.top - pr.height;
    }

    pop.style.position = 'absolute';
    pop.style.left = `${Math.round(left)}px`;
    pop.style.top = `${Math.round(top)}px`;
    pop.style.transform = 'none';
  }, []);

  const checkTargetRect = useCallback(() => {
    const target = triggerRef.current;

    if (!target || !target.getBoundingClientRect) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const last = lastRectRef.current;

    if (
      !last ||
      Math.abs(rect.top - last.top) > 1 ||
      Math.abs(rect.left - last.left) > 1 ||
      Math.abs(rect.width - last.width) > 1 ||
      Math.abs(rect.height - last.height) > 1
    ) {
      lastRectRef.current = rect;
      repositionManual();
    }
  }, [repositionManual]);

  const handleEntered = useCallback(() => {
    repositionManual();
    const target = triggerRef.current;
    lastRectRef.current = target && target.getBoundingClientRect();

    const tick = () => {
      checkTargetRect();
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [repositionManual, checkTargetRect]);

  const handleExited = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    lastRectRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      lastRectRef.current = null;
    };
  }, []);

  const filteredOptions = useMemo(() => {
    if (!search.trim().length) {
      return options;
    }

    return options.filter(
      (i) => i.label.toLowerCase().indexOf(search.toLowerCase()) !== -1,
    );
  }, [options, search]);

  const renderSearch = () => {
    if (!allowSearch) {
      return null;
    }

    return (
      <input
        type="text"
        className="form-control input-search"
        placeholder="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    );
  };

  const renderFilter = () => {
    if (filterElm) {
      return filterElm;
    }

    return (
      <FocusTrap focusTrapOptions={{ clickOutsideDeactivates: true }}>
        <div>
          {renderSearch()}
          <div className="options-container">
            {showAll && (
              <Checkbox
                checked={single ? value === 'all' : value.includes('all')}
                onClick={() => {
                  if (single) {
                    onChange('all');
                  } else {
                    onChange(['all']);
                  }
                }}
              >
                {allLabel}
              </Checkbox>
            )}
            <TableColumnFilterOptions
              options={filteredOptions}
              single={single}
              value={value}
              onChange={onChange}
              showAll={showAll}
            />
          </div>
        </div>
      </FocusTrap>
    );
  };

  return (
    // eslint-disable-next-line
    <div
      className="d-inline-flex ml-2 flex-shrink-0 table-column-filter"
      onClick={(e) => e.stopPropagation()}
    >
      <OverlayTrigger
        trigger="click"
        rootClose
        placement="bottom"
        onEntered={handleEntered}
        onExited={handleExited}
        overlay={
          <Popover
            className="popover-table-column-filter"
            id="popover-table-column-filter"
          >
            <div ref={poInnerRef}>{renderFilter()}</div>
          </Popover>
        }
      >
        <button
          ref={triggerRef}
          type="button"
          className="btn-av-container d-inline-flex align-items-center"
          aria-label={columnName ? `${columnName} filter` : ''}
        >
          <Icon
            icon={ICONS.FILTER}
            className={classNames('av-blue', {
              'av-orange': single
                ? showAll
                  ? value !== 'all'
                  : !!value
                : showAll
                ? !value.includes('all')
                : value.length,
            })}
          />
        </button>
      </OverlayTrigger>
    </div>
  );
}

export function TableColumnFilterOptions({
  options,
  single = true,
  value,
  onChange,
  showAll = false,
}) {
  return options.map((i) => (
    <Checkbox
      key={i.label}
      checked={single ? value === i.value : value.indexOf(i.value) !== -1}
      onClick={(e) => {
        e.preventDefault();

        if (single) {
          onChange(i.value);
        } else {
          let newValue = [...value];

          if (showAll && !single) {
            newValue = newValue.filter((i) => i !== 'all');
          }

          if (!newValue.includes(i.value)) {
            newValue = [...newValue, i.value];
          } else {
            const index = newValue.indexOf(i.value);
            newValue = [
              ...newValue.slice(0, index),
              ...newValue.slice(index + 1),
            ];
          }

          if (!newValue.length && showAll) {
            newValue = ['all'];
          }

          onChange(newValue);
        }
      }}
    >
      {i.label}
    </Checkbox>
  ));
}
